'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { FISSION_SEQ, FISSION_EXTENDED_NAMES, BIOME_LABELS } from '../../muslim-resource-guide/components/fissionData';
import { ECO_TILES, BIOMES, TEMPORAL, CATCOL } from '../../muslim-resource-guide/components/ecoData';
import { AYAT_BRIDGE } from '../../muslim-resource-guide/components/irlData';
import { supabase } from '@/lib/supabaseClient';
import type { EcoTile } from '../../muslim-resource-guide/components/irlData';
import IRLObservationSubmission from './IRLObservationSubmission';

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface CommTile {
  id: string;
  title: string;
  ward: string;
  city: string;
  cat: string;
  names: string[];
  bg: string;
  ayah: string;
  desc: string;
  kkh_sl: string;
  kkh_ed: string;
  kkh_o: string;
  kkh_a: string;
  type: 'comm';
  bridges?: string[];
  bridge_labels?: string[];
}

type AnyTile = EcoTile | CommTile;

interface IRLObsRecord {
  id: string;
  title: string;
  city: string;
  encounter_observations: string[];
  q_is: string;
  q_what: string;
  q_when: string;
  selected_names: string[];
  name_roles: Record<string, string>;
  process_name: string;
  process_steps: string[];
  zahir: string;
  batin: string;
  haqq: string;
  amanah: string;
  mrg_bridges: string[];
  variant: string;
  status: string;
  created_at: string;
}

// ─── NAMES ───────────────────────────────────────────────────────────────────

const BASE_NAMES = Object.entries(AYAT_BRIDGE).map(([key, val]) => ({
  id: key.toLowerCase().replace('al-', '').replace('ar-', '').replace('ash-', '').replace('as-', ''),
  ar: (val as { ar: string; en: string; cat?: string; ayat?: string[]; procs?: string[]; field?: string; sim?: string }).ar,
  en: key,
  m: (val as { ar: string; en: string; cat?: string; ayat?: string[]; procs?: string[]; field?: string; sim?: string }).en,
  cat: (val as { ar: string; en: string; cat?: string }).cat || 'Community',
  ayat: (val as { ar: string; en: string; ayat?: string[] }).ayat || [],
  procs: (val as { ar: string; en: string; procs?: string[] }).procs || [],
  field: (val as { ar: string; en: string; field?: string }).field || '',
  sim: (val as { ar: string; en: string; sim?: string }).sim || '',
}));

const ALL_NAMES = [
  ...BASE_NAMES,
  ...FISSION_EXTENDED_NAMES.map(n => ({
    id: n.id, ar: n.ar, en: n.en, m: n.m, cat: n.cat,
    ayat: n.ayat, procs: n.procs, field: n.field, sim: n.sim,
  })),
];

const CITIES = ['Atlanta', 'Detroit', 'Chicago', 'Pittsburgh', 'Durham', 'Louisville', 'Portland', 'Cincinnati', 'Omaha'];

const COMM_TILES: CommTile[] = [
  { id: 'd1', title: 'Brightmoor Prairie', ward: 'Brightmoor', city: 'Detroit', cat: 'Health', names: ['muhyi', 'khaliq'], bg: '#176b6e', ayah: 'You see the earth barren, but when We send down rain, it quivers and grows. (22:5)', desc: 'Vacant lots in Brightmoor returning to tallgrass prairie.', kkh_sl: 'Prairie succession as divine linguistic pattern.', kkh_ed: 'The environment designs itself when human interference ceases.', kkh_o: 'Annuals first, then perennials, then woody shrubs, then canopy.', kkh_a: 'Brightmoor Farmway collective maintains paths through the rewilding.', type: 'comm' },
  { id: 'd2', title: 'Banglatown Rizq', ward: 'Banglatown', city: 'Detroit', cat: 'Food', names: ['razzaq', 'jami'], bg: '#1a5c5a', ayah: 'Eat of the good things We have provided for you. (2:172)', desc: 'Banglatown halal markets on Conant Street.', kkh_sl: 'Rizq as divine provision language.', kkh_ed: 'The corridor self-organizes around communal need.', kkh_o: 'Immigration, then settlement, then commerce, then community institution.', kkh_a: 'Banglatown merchants association operates informal mutual aid during Ramadan.', type: 'comm' },
  { id: 'c1', title: 'Roseland Transit Desert', ward: 'Roseland', city: 'Chicago', cat: 'Income', names: ['adl', 'sabur'], bg: '#134e50', ayah: 'So be patient. Indeed, the promise of Allah is truth. (30:60)', desc: 'Roseland has waited 60+ years for the Red Line extension.', kkh_sl: 'The unfulfilled promise as justice vocabulary.', kkh_ed: 'Transit deserts design isolation.', kkh_o: 'Promise, then delay, then disinvestment, then renewed advocacy.', kkh_a: 'Roseland community leaders maintain pressure across political generations.', type: 'comm' },
  { id: 'a1', title: 'West End Walking', ward: 'West End', city: 'Atlanta', cat: 'Community', names: ['basir', 'jami'], bg: '#154d4a', ayah: 'And walk upon the earth with humility. (25:63)', desc: 'West End walking circles combining spiritual practice with neighborhood observation.', kkh_sl: 'The walking circle as seeing language.', kkh_ed: 'Walking design creates observation, community, and health simultaneously.', kkh_o: 'Intention, then movement, then observation, then gathering, then action.', kkh_a: 'Walkers document observations for community development planning sessions.', type: 'comm' },
  { id: 'p1', title: 'Hill District Masjid', ward: 'Hill District', city: 'Pittsburgh', cat: 'Housing', names: ['baqi', 'sabur'], bg: '#0f3d42', ayah: 'Whatever is with you will end, but what is with Allah endures. (16:96)', desc: 'Masajid in the Hill District persisting through urban renewal.', kkh_sl: 'The standing masjid as endurance language.', kkh_ed: 'Institutional survival designs community continuity.', kkh_o: 'Community, then demolition, then isolation, then gradual return.', kkh_a: 'Hill District masjid hosts community planning sessions.', type: 'comm' },
  { id: 'l1', title: '9th Street Divide', ward: 'West Louisville', city: 'Louisville', cat: 'Income', names: ['adl', 'haqq'], bg: '#1a5c5a', ayah: 'Indeed, Allah commands justice and good conduct. (16:90)', desc: "Louisville's 9th Street — a physical wall of segregation infrastructure.", kkh_sl: 'The life expectancy statistic as justice language.', kkh_ed: 'Infrastructure design encodes segregation.', kkh_o: 'Redlining, then infrastructure, then disinvestment, then health disparity.', kkh_a: 'West Louisville residents map health outcomes by block.', type: 'comm' },
  { id: 'po1', title: 'Alberta Displacement', ward: 'Alberta Arts', city: 'Portland', cat: 'Community', names: ['haqq', 'shahid'], bg: '#154d4a', ayah: 'And say: Truth has come and falsehood has departed. (17:81)', desc: 'Alberta Arts District — once the heart of Black Portland.', kkh_sl: 'The gallery as truth language.', kkh_ed: 'Arts district design displaces the community that made the culture.', kkh_o: 'Black community, then disinvestment, then artists, then developers, then displacement.', kkh_a: 'Former Alberta residents maintain oral history project.', type: 'comm' },
  { id: 'ci1', title: 'Peebles Corner Void', ward: 'Walnut Hills', city: 'Cincinnati', cat: 'Community', names: ['shahid', 'haqq'], bg: '#0e4a55', ayah: 'We have not neglected in the Register a thing. (6:38)', desc: 'Peebles Corner — where institutions withdrew, leaving civic void.', kkh_sl: 'The empty storefront as witness language.', kkh_ed: 'Institutional withdrawal designs civic desert.', kkh_o: 'Thriving, then withdrawal, then vacancy, then community-led reimagination.', kkh_a: 'Walnut Hills Redevelopment Foundation works to attract services back.', type: 'comm' },
  { id: 'o1', title: '24th and Lake', ward: 'North Omaha', city: 'Omaha', cat: 'Community', names: ['basir', 'shahid'], bg: '#176b6e', ayah: 'Do they not look at the birds above them? (67:19)', desc: '24th and Lake — once the hub of Black Omaha.', kkh_sl: 'The intersection as seeing language.', kkh_ed: 'Historic intersection design anchors community identity.', kkh_o: 'Prosperity, then neglect, then partial demolition, then memory work.', kkh_a: "Love's Jazz Center maintains cultural presence at the historic intersection.", type: 'comm' },
];

const ALL_TILES: AnyTile[] = [...COMM_TILES, ...ECO_TILES];

const LAYER_LABELS = [
  'ENCOUNTER', 'THREE QUESTIONS', 'NAMES FISSION', 'SUNNAT ALLAH',
  'ZAHIR', 'BATIN', 'HAQQ', 'AMANAH + BRIDGES',
];

// ─── LEAF SEQUENCE TUTORIAL ───────────────────────────────────────────────────

const LEAF_SEQUENCE = [
  { step: 1, name: 'Al-Qadir', ar: 'الْقَادِر', meaning: 'The All-Powerful', color: '#c9a84c', note: 'Recognition begins with acknowledging divine capacity — the power behind every observable process. Before the leaf, there is Al-Qadir.' },
  { step: 2, name: 'Al-Khaliq', ar: 'الْخَالِق', meaning: 'The Creator', color: '#c9a84c', note: 'From power comes creation — the origination of form from nothing. The leaf did not design itself. Al-Khaliq originates.' },
  { step: 3, name: 'Al-Bari', ar: 'الْبَارِئ', meaning: 'The Evolver', color: '#2d7a47', note: 'The fashioning of distinct form — each leaf shaped precisely, no two identical. Bari separates and distinguishes form from formlessness.' },
  { step: 4, name: 'Al-Musawwir', ar: 'الْمُصَوِّر', meaning: 'The Fashioner of Forms', color: '#2d7a47', note: 'The imaging — the visible form given to the created thing. The color, the veins, the texture. Zahir made manifest through Al-Musawwir.' },
  { step: 5, name: 'Ar-Razzaq', ar: 'الرَّزَّاق', meaning: 'The Ever Provider', color: '#2d8a87', note: 'The leaf is a provision engine — photosynthesis as Ar-Razzaq\'s mechanism. Rizq flows through the chlorophyll. The leaf feeds the world.' },
  { step: 6, name: 'An-Nur', ar: 'النُّور', meaning: 'The Light', color: '#c9a84c', note: 'The sequence closes with light — the leaf is a receiver of An-Nur, converting divine light into sustenance. The arc completes. Alhamdulillah.' },
];

// ─── RECOGNITION ARC STAGES ───────────────────────────────────────────────────

const RECOGNITION_ARC_STAGES = [
  { id: 'mata', label: 'MATA', ar: 'مَاطَا', desc: 'The Lens as Tool — the instrument of observation. Mata is the methodology: how you look determines what you see. The IRL Lens is your Mata.' },
  { id: 'encounter', label: 'ENCOUNTER', ar: 'مشاهدة', desc: 'Initial sensory contact with creation. What your eyes, ears, nose, skin perceive. The zahir of the thing.' },
  { id: 'examination', label: 'EXAMINATION', ar: 'فحص', desc: 'Deliberate investigation using the Three Questions. Is it? What is it? When is it? Examination is the bridge between raw encounter and named recognition.' },
  { id: 'recognition', label: 'RECOGNITION', ar: 'معرفة', desc: 'The Names become visible. You see Al-Khaliq in the leaf, Ar-Razzaq in the river, Al-Adl in the ecosystem. Recognition is the purpose of the Lens.' },
  { id: 'reassembly', label: 'RE-ASSEMBLY', ar: 'إعادة التركيب', desc: 'The return to original design — seeing the thing as it was always meant to be. The Names reveal the blueprint. Re-Assembly is the recognition that creation is already complete.' },
  { id: 'uboor', label: 'UBOOR', ar: 'عُبُور', desc: 'Crossing over — from recognition to action. The Amanah is accepted. The bridge to the Muslim Resource Guide. Breathe. Alhamdulillah.' },
];

// ─── OBS PANEL WRAPPER ────────────────────────────────────────────────────────

function ObsPanel({ open, onClose, cityId, variant }: { open: boolean; onClose: () => void; cityId: string; variant: string }) {
  if (!open) return null;
  return (
    <IRLObservationSubmission
      open={open}
      onClose={onClose}
      cityId={cityId}
      variant={variant}
    />
  );
}

// ─── WALKTHROUGH PANEL ────────────────────────────────────────────────────────

function WalkthroughPanel({ onClose }: { onClose: () => void }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [prevStep, setPrevStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  // Simulate initial load
  useEffect(() => {
    const t = setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => setContentVisible(true), 80);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const [activeBridge, setActiveBridge] = useState<string | null>(null);
  const [activeLayer, setActiveLayer] = useState(-1);
  const [visitedLayers, setVisitedLayers] = useState<Set<number>>(new Set());
  const [enteringLayer, setEnteringLayer] = useState<number | null>(null);
  const [showBatin, setShowBatin] = useState(false);
  const [tempActive, setTempActive] = useState(5);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughDismissed, setWalkthroughDismissed] = useState(false);
  // Supabase live
  const [observations, setObservations] = useState<IRLObsRecord[]>([]);
  const [latestObsId, setLatestObsId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [newObsCount, setNewObsCount] = useState(0);
  const catalogScrollRef = useRef<HTMLDivElement>(null);

  const mRef = useRef<HTMLDivElement>(null);
  const mosaicRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const tileRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const layerRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];

  const [obsOpen, setObsOpen] = useState(false);

  // ── Supabase: initial load + realtime subscription ──
  useEffect(() => {
    async function loadObservations() {
      const { data, error } = await supabase
        .from('irl_observations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (!error && data) setObservations(data as IRLObsRecord[]);
    }
    loadObservations();

    const channel = supabase
      .channel('irl_observations_live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'irl_observations' }, (payload) => {
        const newObs = payload.new as IRLObsRecord;
        setObservations(prev => [newObs, ...prev]);
        setLatestObsId(newObs.id);
        setNewObsCount(prev => prev + 1);
        // Toast notification
        const toastId = `toast-${Date.now()}`;
        const names = Array.isArray(newObs.selected_names) ? newObs.selected_names : [];
        const nameStr = names.slice(0, 3).map(nid => {
          const n = ALL_NAMES.find(x => x.id === nid);
          return n ? n.ar : '';
        }).filter(Boolean).join(' · ');
        setToasts(prev => [...prev, {
          id: toastId,
          title: newObs.title || 'New Observation',
          sub: `${newObs.city || 'Unknown city'} · ${nameStr || 'No names extracted'}`,
        }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 5000);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    function measure() {
      if (mRef.current) {
        const w = mRef.current.offsetWidth;
        const h = mRef.current.offsetHeight;
        const containerW = Math.min(w * 0.98, 720);
        const containerH = Math.min(h * 0.54, 560);
        setSz(Math.min(containerW * 0.96, containerH * 0.56, 680));
      }
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selTile) return;
    function onKey(e: KeyboardEvent) {
      if (variant !== 'v2') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goToLayer(Math.min(7, activeLayer + 1)); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goToLayer(Math.max(0, activeLayer - 1)); }
      else if (e.key === 'Escape') { setSelTile(null); setActiveLayer(-1); setActiveBridge(null); }
      else if (e.key >= '1' && e.key <= '8') { goToLayer(parseInt(e.key) - 1); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selTile, activeLayer, variant]);

  function goToLayer(idx: number) {
    setActiveLayer(idx);
    setVisitedLayers(prev => new Set([...prev, idx]));
    setEnteringLayer(idx);
    setTimeout(() => setEnteringLayer(null), 300);
    if (layerRefs[idx]?.current && scrollRef.current) {
      layerRefs[idx].current!.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  const biome = BIOMES[cityId] || { name: '', ar: '' };
  const isPhys = (t: typeof FISSION_SEQ[0]) => t.biome === 'physics' || t.biome === 'quantum';

  const v1CommTiles = COMM_TILES.filter(t => t.city === cityId);
  const v1EcoTiles = ECO_TILES.filter(t => t.city === cityId);
  const v1Tiles: AnyTile[] = filter === 'eco' ? v1EcoTiles : filter === 'comm' ? v1CommTiles : [...v1CommTiles, ...v1EcoTiles];

  const v2Tiles = FISSION_SEQ.filter(t => {
    const locMatch = t.loc === cityId || t.loc.includes('All') || t.loc === 'Physics' || t.loc === 'Quantum' || t.loc === 'Atmospheric';
    if (filter === 'phys') return isPhys(t);
    if (filter === 'eco') return locMatch && !isPhys(t);
    return locMatch;
  }).sort((a, b) => a.seq - b.seq);

  const displayTiles = variant === 'v1' ? v1Tiles : v2Tiles;

  const ringW = sz * 0.42;
  const envW = sz * 0.28;

  const activeNameIds: Record<string, boolean> = {};
  displayTiles.forEach(t => t.names.forEach(n => { activeNameIds[n] = true; }));
  const ringNames = ALL_NAMES.filter(n => activeNameIds[n.id]);

  function makeRows() {
    const total = displayTiles.length;
    const r1End = Math.ceil(total * 0.35);
    const r2End = r1End + Math.ceil(total * 0.15);
    const r3End = r2End + Math.ceil(total * 0.25);
    return [
      { tiles: displayTiles.slice(0, r1End), y: 0, h: sz * 0.16, label: variant === 'v2' ? 'ENCOUNTERS' : 'WARD TILES', speck: false },
      { tiles: displayTiles.slice(r1End, r2End), y: sz * 0.18, h: sz * 0.12, label: '', speck: true },
      { tiles: displayTiles.slice(r2End, r3End), y: sz * 0.65, h: sz * 0.16, label: variant === 'v2' ? 'PROCESSES' : 'AYAT DOMAINS', speck: false },
      { tiles: displayTiles.slice(r3End), y: sz * 0.83, h: sz * 0.15, label: variant === 'v2' ? 'PHYSICS LAYER' : 'BRIDGE HAALS', speck: false },
    ];
  }

  function handleTile(t: AnyTile | typeof FISSION_SEQ[0]) {
    if (selTile?.id === t.id) { setSelTile(null); setActiveLayer(-1); setActiveBridge(null); return; }
    if (variant === 'v2' && 'biome' in t && ((t as typeof FISSION_SEQ[0]).biome === 'physics' || (t as typeof FISSION_SEQ[0]).biome === 'quantum')) {
      if (typeof window !== 'undefined' && !localStorage.getItem('haal_physics_guide_seen')) {
        setShowPhysGuide(true);
      }
    }
    setSelTile(t);
    setSelName(null);
    setNameOpen(false);
    setActiveBridge(null);
    setShowBatin(false);
    if (variant === 'v2') { setActiveLayer(0); setVisitedLayers(new Set([0])); }
  }

  function handleName(n: typeof ALL_NAMES[0]) {
    setSelName(n); setNameOpen(true); setSelTile(null); setActiveLayer(-1);
  }

  function handleNameById(nid: string) {
    const n = ALL_NAMES.find(x => x.id === nid);
    if (n) { setActiveTab('lens'); handleName(n); }
  }

  function handleBridge(targetId: string) {
    setActiveBridge(null);
    if (variant === 'v2') {
      const target = FISSION_SEQ.find(t => t.id === targetId);
      if (target) { setTimeout(() => { setSelTile(target); setActiveLayer(0); setVisitedLayers(new Set([0])); }, 100); }
    } else {
      const target = ALL_TILES.find(t => t.id === targetId);
      if (target) {
        if ('city' in target && target.city !== cityId) setCityId(target.city);
        setTimeout(() => { setSelTile(target); setFilter('all'); }, 100);
      }
    }
  }

  function handleFissionTileClick(tile: typeof FISSION_SEQ[0]) {
    setActiveTab('lens');
    setVariant('v2');
    setSelTile(tile);
    setActiveLayer(0);
    setVisitedLayers(new Set([0]));
  }

  function handleSearchSelect(tile: AnyTile | typeof FISSION_SEQ[0]) {
    setSearchQuery('');
    setShowSearch(false);
    if ('biome' in tile) {
      setVariant('v2');
      setSelTile(tile);
      setActiveLayer(0);
      setVisitedLayers(new Set([0]));
    } else {
      setVariant('v1');
      if ('city' in tile) setCityId(tile.city);
      setSelTile(tile);
    }
  }

  function getTileCenter(tileId: string): { x: number; y: number } | null {
    const el = tileRefs.current[tileId];
    const mosaic = mosaicRef.current;
    if (!el || !mosaic) return null;
    const elRect = el.getBoundingClientRect();
    const mosaicRect = mosaic.getBoundingClientRect();
    return { x: elRect.left - mosaicRect.left + elRect.width / 2, y: elRect.top - mosaicRect.top + elRect.height / 2 };
  }

  const srcPos = selTile ? getTileCenter(selTile.id) : null;
  const tgtPos = activeBridge ? getTileCenter(activeBridge) : null;
  const activeBridgeObj = selTile && 'bridges' in selTile ? (selTile as typeof FISSION_SEQ[0]).bridges.find(b => b.id === activeBridge) : null;

  const mosaicW = typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.98, 720) : 720;
  const mosaicH = typeof window !== 'undefined' ? Math.min(window.innerHeight * 0.54, 560) : 560;

  const ct = selTile;
  const isFissionTile = ct && 'biome' in ct;

  function renderV1Detail(ct: AnyTile) {
    return (
      <div key={ct.id}>
        {/* V1/V2 SYNTHESIS NOTE */}
        <div style={{ padding: '5px 8px', background: 'rgba(45,138,135,0.05)', border: '1px solid rgba(45,138,135,0.1)', borderRadius: 4, marginBottom: 8, fontSize: 9, color: '#7a6f5d', lineHeight: 1.5 }}>
          <span style={{ color: '#2d8a87', marginRight: 4 }}>V1 · 7-LAYER SCROLL</span>
          This tile uses the 7-layer community lens. Switch to V2 to explore the same Names through the 8-layer fission pathway.
        </div>
        <div className="irl-u-dh">
          <div>
            <div className="irl-u-dt">{ct.title}</div>
            <div className="irl-u-ds">
              {'ward' in ct ? `${ct.ward} · ` : ''}{'city' in ct ? ct.city : cityId} · <span style={{ color: CATCOL[ct.cat] || '#d4c5a9' }}>{ct.cat}</span>
              <span style={{ display: 'inline-block', padding: '1px 4px', borderRadius: 2, fontSize: 6, marginLeft: 4, background: ct.type === 'eco' ? 'rgba(26,74,46,0.2)' : 'rgba(26,92,90,0.1)', color: ct.type === 'eco' ? '#2d7a47' : '#2d8a87', border: `1px solid ${ct.type === 'eco' ? 'rgba(45,122,71,0.2)' : 'rgba(45,138,135,0.2)'}` }}>
                {ct.type === 'eco' ? 'AYAT IN CREATION' : 'COMMUNITY'}
              </span>
            </div>
          </div>
          <button className="irl-u-dx" onClick={() => setSelTile(null)}>Close</button>
        </div>
        <div style={{ margin: '8px 0' }}>
          <div className="irl-u-dlb">ZAHIR — الظاهر</div>
          <div className="irl-u-dtx">{'desc' in ct ? ct.desc : 'encounter' in ct ? ct.encounter : ''}</div>
        </div>
        <div style={{ margin: '8px 0', padding: '10px', background: 'rgba(10,25,41,0.2)', borderRadius: 4, border: '1px solid rgba(201,168,76,0.08)' }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', marginBottom: 6, textTransform: 'uppercase' }}>THE THREE QUESTIONS — ثلاثة أسئلة</div>
          <div className="irl-u-tq">
            <div className="irl-u-tql">حدوث ذاتي — IS IT?</div>
            <div className="irl-u-tqt">Yes — this exists as a {ct.cat.toLowerCase()} creation, an Ayah of {ct.names.map(nid => { const n = ALL_NAMES.find(x => x.id === nid); return n ? n.en : nid; }).filter(Boolean).join(', ')}, and an Amanah placed in this region.</div>
          </div>
          {'kkh_sl' in ct && <div className="irl-u-tq" style={{ borderLeftColor: '#2d8a87' }}>
            <div className="irl-u-tql" style={{ color: '#2d8a87' }}>حدوث صفاتي — WHAT IS IT?</div>
            <div className="irl-u-tqt">{ct.kkh_sl}</div>
          </div>}
          {'kkh_o' in ct && <div className="irl-u-tq" style={{ borderLeftColor: '#2d7a47' }}>
            <div className="irl-u-tql" style={{ color: '#2d7a47' }}>حدوث زماني — WHEN IS/WAS IT?</div>
            <div className="irl-u-tqt">{ct.kkh_o}</div>
          </div>}
        </div>
        <div style={{ margin: '8px 0' }}>
          <div className="irl-u-dlb">SUNNAT ALLAH — سنة الله</div>
          <div className="irl-u-day">{ct.ayah}</div>
          {ct.names.map(nid => {
            const n = ALL_NAMES.find(x => x.id === nid);
            if (!n) return null;
            const fissionTiles = FISSION_SEQ.filter(t => t.names.includes(nid)).slice(0, 3);
            return (
              <div key={nid}>
                <div className="irl-u-fc" onClick={() => setSelTile(n)}>
                  <div className="irl-u-fn">{n.ar}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#c9a84c', fontWeight: 'bold' }}>{n.en} — {n.m}</div>
                    <div style={{ fontSize: 11, color: '#d4c5a9', marginTop: 2 }}>{n.sim || n.field}</div>
                    <div style={{ fontSize: 10, color: '#2d8a87', marginTop: 2 }}>→ {n.cat} resources in MRG</div>
                  </div>
                </div>
                {fissionTiles.length > 0 && (
                  <div style={{ marginLeft: 48, marginTop: -2, marginBottom: 4 }}>
                    <div style={{ fontSize: 7, color: '#7a6f5d', letterSpacing: '.06em', marginBottom: 2 }}>ALSO IN V2 FISSION:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {fissionTiles.map(ft => (
                        <div key={ft.id}
                          style={{ padding: '4px 6px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(90,90,138,0.1)', borderRadius: 3, cursor: 'pointer', fontSize: 11, color: '#d4c5a9' }}
                          onClick={() => { setVariant('v2'); setSelTile(ft); setActiveLayer(0); setVisitedLayers(new Set([0])); }}>
                          <span style={{ color: '#5a5a8a', fontSize: 9 }}>s{ft.seq} </span>{ft.title} <span style={{ color: '#7a6f5d' }}>({ft.loc})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="irl-u-sep" />
        {'kkh_ed' in ct && <div className="irl-u-kkh"><div className="irl-u-kt">ED — AYAT IN CREATION</div><div className="irl-u-kd">{ct.kkh_ed}</div></div>}
        <div style={{ margin: '8px 0', padding: '10px', background: 'linear-gradient(135deg,rgba(26,92,90,0.06),rgba(201,168,76,0.04))', borderRadius: 4, border: '1px solid rgba(201,168,76,0.12)', textAlign: 'center' }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', marginBottom: 6, textTransform: 'uppercase' }}>حق — HAQQ</div>
          <div style={{ fontSize: 14, color: '#d4c5a9', lineHeight: 1.6, fontStyle: 'italic' }}>
            {'haqq' in ct ? ct.haqq : `This ${ct.cat} in ${'city' in ct ? ct.city : cityId} is an Ayah pointing to ${ct.names.map(nid => { const n = ALL_NAMES.find(x => x.id === nid); return n ? n.en : nid; }).filter(Boolean).join(', ')}.`}
          </div>
        </div>
        <div className="irl-u-kkh">
          <div className="irl-u-kt">أمانة — AMANAH</div>
          <div className="irl-u-kd">{'kkh_a' in ct ? ct.kkh_a : 'amanah' in ct ? ct.amanah : ''}</div>
        </div>
      </div>
    );
  }

  function renderV2Detail(ct: typeof FISSION_SEQ[0]) {
    const layerClass = (idx: number) => {
      let cls = `irl-u-layer irl-u-l${idx + 1}`;
      if (activeLayer === idx) cls += ' irl-u-layer-active';
      else if (activeLayer !== -1) cls += ' irl-u-layer-inactive';
      if (enteringLayer === idx) cls += ' irl-u-layer-entering';
      return cls;
    };
    const numClass = (idx: number) => `irl-u-lnum${activeLayer === idx ? ' irl-u-lnum-active' : ''}`;
    const bridgePillClass = (type: string) => {
      if (type === 'name') return 'irl-u-bridge-pill irl-u-pill-name';
      if (type === 'process') return 'irl-u-bridge-pill irl-u-pill-process';
      if (type === 'cycle') return 'irl-u-bridge-pill irl-u-pill-cycle';
      return 'irl-u-bridge-pill irl-u-pill-amanah';
    };
    const bridgePillLabel = (type: string) => {
      if (type === 'name') return 'SHARED NAME';
      if (type === 'process') return 'SAME PROCESS';
      if (type === 'cycle') return 'SHARED CYCLE';
      return 'SHARED TRUST';
    };

    // V1 community tiles sharing same Names
    const sharedV1Tiles = ALL_TILES.filter(t => t.names.some(nid => ct.names.includes(nid))).slice(0, 4);

    return (
      <div key={ct.id}>
        {/* V1/V2 SYNTHESIS NOTE */}
        <div style={{ padding: '5px 8px', background: 'rgba(90,90,138,0.05)', border: '1px solid rgba(90,90,138,0.1)', borderRadius: 4, marginBottom: 8, fontSize: 9, color: '#7a6f5d', lineHeight: 1.5 }}>
          <span style={{ color: '#5a5a8a', marginRight: 4 }}>V2 · 8-LAYER FISSION</span>
          Navigate layers with ← → arrows or keys 1–8. Switch to V1 to see these Names in community context.
          {sharedV1Tiles.length > 0 && (
            <span style={{ marginLeft: 6 }}>
              V1 tiles sharing these Names:
              {sharedV1Tiles.map(t => (
                <button key={t.id} onClick={() => { setVariant('v1'); if ('city' in t) setCityId(t.city); setSelTile(t); }}
                  style={{
                    marginLeft: 6,
                    padding: '0 4px',
                    border: '1px solid rgba(45,138,135,0.25)',
                    borderRadius: 2,
                    background: 'transparent',
                    color: '#2d8a87',
                    fontSize: 8,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}>
                  {t.title}
                </button>
              ))}
            </span>
          )}
        </div>
        <div className="irl-u-dh">
          <div>
            <div className="irl-u-dt">{ct.title}</div>
            <div className="irl-u-ds">{ct.loc} · {BIOME_LABELS[ct.biome] || ct.biome} · <span style={{ color: CATCOL[ct.cat] || '#d4c5a9' }}>{ct.cat}</span></div>
          </div>
          <button className="irl-u-dx" onClick={() => { setSelTile(null); setActiveLayer(-1); setActiveBridge(null); }}>Close</button>
        </div>

        {/* RECOGNITION ARC STAGE INDICATOR */}
        <div style={{ marginBottom: 8, padding: '4px 0' }}>
          {/* Progression line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative' }}>
            {RECOGNITION_ARC_STAGES.map((stage, i) => {
              const isActive = (activeLayer === 0 && stage.id === 'encounter') ||
                (activeLayer === 1 && stage.id === 'examination') ||
                ([2, 3].includes(activeLayer) && stage.id === 'recognition') ||
                ([4, 5, 6].includes(activeLayer) && stage.id === 'recognition') ||
                (activeLayer === 7 && stage.id === 'uboor') ||
                (activeLayer === -1 && stage.id === 'mata');
              const isPast = (stage.id === 'mata' && activeLayer >= 0) ||
                (stage.id === 'encounter' && activeLayer > 0) ||
                (stage.id === 'examination' && activeLayer > 1) ||
                (stage.id === 'recognition' && activeLayer > 3) ||
                (stage.id === 'reassembly' && activeLayer > 6);
              const isUboor = stage.id === 'uboor';
              return (
                <React.Fragment key={stage.id}>
                  <div style={{
                    flex: 1, padding: '3px 4px',
                    background: isActive ? (isUboor ? 'rgba(45,122,71,0.15)' : 'rgba(201,168,76,0.12)') : isPast ? 'rgba(201,168,76,0.05)' : 'rgba(6,13,18,0.3)',
                    border: `1px solid ${isActive ? (isUboor ? 'rgba(45,122,71,0.5)' : 'rgba(201,168,76,0.5)') : isPast ? 'rgba(201,168,76,0.2)' : 'rgba(61,54,39,0.2)'}`,
                    borderRadius: 3, textAlign: 'center',
                    transition: 'all 0.35s ease',
                    boxShadow: isActive ? (isUboor ? '0 0 8px rgba(45,122,71,0.25)' : '0 0 8px rgba(201,168,76,0.2)') : 'none',
                    position: 'relative',
                  }}>
                    {/* Active pulse dot */}
                    {isActive && (
                      <div style={{
                        position: 'absolute', top: -3, right: -3, width: 6, height: 6,
                        borderRadius: '50%',
                        background: isUboor ? '#2d7a47' : '#c9a84c',
                        boxShadow: isUboor ? '0 0 6px rgba(45,122,71,0.8)' : '0 0 6px rgba(201,168,76,0.8)',
                        animation: 'irl-dot-pulse 2s ease-in-out infinite',
                      }} />
                    )}
                    <div style={{ fontSize: 6, color: isActive ? (isUboor ? '#2d7a47' : '#c9a84c') : isPast ? 'rgba(201,168,76,0.5)' : '#7a6f5d', letterSpacing: '.06em', textTransform: 'uppercase', lineHeight: 1.2 }}>{stage.label}</div>
                    <div style={{ fontSize: 8, color: isActive ? (isUboor ? 'rgba(45,122,71,0.8)' : 'rgba(201,168,76,0.7)') : isPast ? 'rgba(201,168,76,0.35)' : 'rgba(122,111,93,0.4)' }}>{stage.ar}</div>
                  </div>
                  {/* Progression line between stages */}
                  {i < RECOGNITION_ARC_STAGES.length - 1 && (
                    <div style={{
                      width: 6, height: 1, flexShrink: 0,
                      background: isPast ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)',
                      transition: 'background 0.4s ease',
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div ref={layerRefs[0]} className={layerClass(0)}>
          <div className="irl-u-lh"><div className={numClass(0)}>1</div>ENCOUNTER — مشاهدة</div>
          <div className="irl-u-ltx">{ct.encounter}</div>
        </div>
        <div ref={layerRefs[1]} className={layerClass(1)}>
          <div className="irl-u-lh"><div className={numClass(1)}>2</div>THE THREE QUESTIONS — ثلاثة أسئلة</div>
          <div style={{ fontSize: 9, color: '#7a6f5d', marginBottom: 6, lineHeight: 1.5 }}>
            Examination is the deliberate application of the Three Questions — the onboarding methodology of the IRL Lens. Each question opens a different dimension of the encounter.
          </div>
          <div className="irl-u-tq"><div className="irl-u-tql">حدوث ذاتي — IS IT?</div><div className="irl-u-tqt">{ct.threeQ.is}</div></div>
          <div className="irl-u-tq" style={{ borderLeftColor: '#2d8a87' }}><div className="irl-u-tql" style={{ color: '#2d8a87' }}>حدوث صفاتي — WHAT IS IT?</div><div className="irl-u-tqt">{ct.threeQ.what}</div></div>
          <div className="irl-u-tq" style={{ borderLeftColor: '#2d7a47' }}><div className="irl-u-tql" style={{ color: '#2d7a47' }}>حدوث زماني — WHEN IS/WAS IT?</div><div className="irl-u-tqt">{ct.threeQ.when}</div></div>
          <div style={{ margin: '4px 0' }}>
            <div className="irl-u-temp-strip">
              {TEMPORAL.map((stage, i) => (
                <button key={stage.k} onClick={() => setTempActive(i)} className={`irl-u-temp-pill${i === tempActive ? ' irl-u-temp-active' : ''}`}>
                  <div style={{ fontSize: 9, color: i === tempActive ? '#c9a84c' : '#7a6f5d', letterSpacing: '.04em' }}>{stage.ar}</div>
                  <div style={{ fontSize: 6, color: i === tempActive ? '#c9a84c' : '#7a6f5d', letterSpacing: '.04em' }}>{stage.k}</div>
                </button>
              ))}
            </div>
            {TEMPORAL[tempActive] && (
              <div style={{ padding: '4px 6px', background: 'rgba(6,13,18,0.3)', borderRadius: 3, marginTop: 2 }}>
                <div style={{ fontSize: 9, color: '#c9a84c', fontWeight: 'bold' }}>{TEMPORAL[tempActive].k} — {TEMPORAL[tempActive].ar}</div>
                <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.4 }}>{TEMPORAL[tempActive].d}</div>
              </div>
            )}
          </div>
        </div>
        <div ref={layerRefs[2]} className={layerClass(2)}>
          <div className="irl-u-lh"><div className={numClass(2)}>3</div>NAMES — الأسماء — FISSION</div>
          {ct.names_detail.map((nd, i) => {
            const nameObj = ALL_NAMES.find(n => n.ar === nd.ar || n.en === nd.name);
            return (
              <div key={i} className="irl-u-fc" onClick={() => nameObj && handleName(nameObj)}>
                <div className="irl-u-fn">{nd.ar}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#c9a84c', marginBottom: 3 }}>{nd.name}</div>
                  <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.45 }}>{nd.why}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div ref={layerRefs[3]} className={layerClass(3)}>
          <div className="irl-u-lh"><div className={numClass(3)}>4</div>SUNNAT ALLAH — سنة الله</div>
          <div className="irl-u-day">{ct.ayah}</div>
          {ct.sunnat.map((step, i) => (
            <div key={i} className="irl-u-ss">
              <div className="irl-u-sn">{i + 1}</div>
              <div>{step}</div>
            </div>
          ))}
        </div>

        {/* ZAHIR / BATIN — PRIMARY AXIS (Layers 5–6) */}
        <div style={{ margin: '6px 0', padding: '4px 6px', background: 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.15)', borderRadius: 4, fontSize: 8, color: '#5a5a8a', letterSpacing: '.08em', textAlign: 'center' }}>
          ظاهر ↔ باطن · ZAHIR / BATIN — PRIMARY AXIS OF THE 8-LAYER DEPTH PANEL
        </div>
        <div ref={layerRefs[4]} className={layerClass(4)}>
          <div className="irl-u-lh"><div className={numClass(4)}>5</div>ZAHIR — الظاهر · THE APPARENT</div>
          <div style={{ fontSize: 8, color: '#5a5a8a', marginBottom: 5, letterSpacing: '.06em' }}>Surface reality — what is perceivable, measurable, observable. The face of the thing.</div>
          <div className="irl-u-ltx">{ct.zahir || 'What is apparent to your senses in this encounter?'}</div>
        </div>
        <div ref={layerRefs[5]} className={layerClass(5)}>
          <div className="irl-u-lh"><div className={numClass(5)}>6</div>BATIN — الباطن · THE HIDDEN</div>
          <div style={{ fontSize: 8, color: '#5a5a8a', marginBottom: 5, letterSpacing: '.06em' }}>Hidden reality — the unseen forces, divine decree, and Names operating beneath the surface.</div>
          <div className="irl-u-ltx">{showBatin ? ct.batin : (ct.zahir || 'What is apparent to your senses?')}</div>
          <button className="irl-u-zahir-batin-toggle" onClick={() => setShowBatin(!showBatin)}>
            {showBatin ? 'ظاهر ↔ باطن — Show Zahir' : 'ظاهر ↔ باطن — Reveal Batin'}
          </button>
        </div>

        <div ref={layerRefs[6]} className={layerClass(6)}>
          <div className="irl-u-lh"><div className={numClass(6)}>7</div>HAQQ — حق</div>
          <div style={{ fontSize: 14, color: '#d4c5a9', lineHeight: 1.6, fontStyle: 'italic', textAlign: 'center', padding: '4px 0' }}>{ct.haqq}</div>
        </div>

        {/* UBOOR — LAYER 8 with Breathe + Alhamdulillah */}
        <div ref={layerRefs[7]} className={layerClass(7)}>
          <div className="irl-u-lh"><div className={numClass(7)}>8</div>UBOOR — عُبُور · AMANAH — أمانة · BRIDGES</div>
          <div style={{ fontSize: 8, color: '#2d7a47', marginBottom: 6, letterSpacing: '.06em', lineHeight: 1.5 }}>
            Uboor — crossing over. The recognition arc completes here. The Amanah is accepted. The bridge to action opens.
          </div>
          <div className="irl-u-ltx" style={{ marginBottom: 8 }}>{ct.amanah}</div>

          {/* Re-Assembly note */}
          <div style={{ margin: '8px 0', padding: '8px 12px', background: 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.15)', borderRadius: 5 }}>
            <div style={{ fontSize: 8, color: '#8a8ab8', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>RE-ASSEMBLY — إعادة التركيب</div>
            <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.6 }}>
              The return to original design. What you have recognized through the Lens is not new — it was always there. Re-Assembly is the moment you see the thing as it was always meant to be seen: as an Ayah, a sign, a composition of divine Names.
            </div>
          </div>

          {/* Breathe meditation */}
          <div style={{ margin: '8px 0', padding: '10px 12px', background: 'rgba(26,74,46,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>✦ BREATHE — تنفس</div>
            <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.7, fontStyle: 'italic' }}>
              Pause here. You have moved through all 8 layers of this encounter. The recognition is complete. What you have seen is real. What you have named is true. The Amanah now rests with you.
            </div>
          </div>

          {/* Universal Submission */}
          <div style={{ margin: '8px 0', padding: '10px 12px', background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 6 }}>
            <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>UNIVERSAL SUBMISSION</div>
            <div style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.7 }}>
              Every particle submits. The leaf submits to Al-Qadir. The river submits to Ar-Razzaq. The ecosystem submits to Al-Mizan. The Lens reveals this submission — it is already happening, everywhere, in everything. You are invited to join what creation has never stopped doing.
            </div>
          </div>

          {/* Alhamdulillah closing */}
          <div style={{
            marginTop: 10, padding: '10px 14px',
            background: 'linear-gradient(135deg, rgba(201,168,76,0.14), rgba(45,122,71,0.08))',
            border: '1px solid rgba(201,168,76,0.5)',
            borderRadius: 6, textAlign: 'center',
            boxShadow: '0 0 24px rgba(201,168,76,0.15), inset 0 0 30px rgba(201,168,76,0.05)',
          }}>
            <div style={{ fontSize: 26, color: '#c9a84c', fontFamily: 'Georgia, serif', marginBottom: 4, textShadow: '0 0 16px rgba(201,168,76,0.5)' }}>الْحَمْدُ لِلَّه</div>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.8)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>✦ Alhamdulillah</div>
            <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)' }}>The arc is complete — every recognition returns to gratitude</div>
          </div>

          {ct.bridges.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.08em', marginBottom: 4 }}>BRIDGE CONNECTIONS</div>
              {ct.bridges.map((b, i) => (
                <button key={i} className="irl-u-bl"
                  onMouseEnter={() => setActiveBridge(b.id)}
                  onMouseLeave={() => setActiveBridge(null)}
                  onClick={() => { setActiveBridge(null); handleBridge(b.id); }}>
                  <span className={bridgePillClass(b.type)}>{bridgePillLabel(b.type)}</span>
                  {b.label.length > 30 ? b.label.slice(0, 30) + '…' : b.label}
                </button>
              ))}
            </div>
          )}
          {/* Community observations sharing these Names */}
          {observations.length > 0 && (() => {
            const relatedObs = observations.filter(o => {
              const names = Array.isArray(o.selected_names) ? o.selected_names : [];
              return names.some(nid => ct.names.includes(nid));
            }).slice(0, 3);
            if (relatedObs.length === 0) return null;
            return (
              <div style={{ marginTop: 10, padding: '6px 8px', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 4, background: 'rgba(26,92,90,0.04)' }}>
                <div style={{ fontSize: 7, color: '#2d8a87', letterSpacing: '.08em', marginBottom: 4 }}>COMMUNITY OBSERVATIONS SHARING THESE NAMES</div>
                {relatedObs.map(o => (
                  <div key={o.id} style={{ fontSize: 10, color: '#7a6f5d', padding: '2px 0', borderTop: '1px solid rgba(61,54,39,0.15)' }}>
                    <span style={{ color: '#d4c5a9' }}>{o.title}</span> · {o.city}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  const handleStepChange = (newStep: number) => {
    if (newStep === activeStep || transitioning) return;
    setTransitioning(true);
    setContentVisible(false);
    setPrevStep(activeStep);
    setTimeout(() => {
      setActiveStep(newStep);
      setTimeout(() => {
        setContentVisible(true);
        setTransitioning(false);
      }, 80);
    }, 200);
  };

  return (
    <>
      <style>{`
        /* ── IRL Unified Screen Base ── */
        .irl-u { display:flex; flex-direction:column; background:#060d12; color:#d4c5a9; font-family:'Courier New',monospace; font-size:11px; min-height:600px; position:relative; }
        .irl-u-hd { display:flex; align-items:center; gap:12px; padding:10px 16px; background:rgba(6,13,18,0.98); border-bottom:1px solid rgba(61,54,39,0.4); flex-wrap:wrap; }
        .irl-u-hd-t { font-size:11px; letter-spacing:.18em; color:#c9a84c; text-transform:uppercase; font-weight:700; }
        .irl-u-hd-s { font-size:8px; letter-spacing:.1em; color:#7a6f5d; text-transform:uppercase; margin-top:2px; }
        .irl-u-hd-ar { font-size:14px; color:rgba(201,168,76,0.4); margin-left:auto; font-family:'Amiri',Georgia,serif; direction:rtl; }
        .irl-u-btn { padding:3px 10px; background:transparent; border:1px solid rgba(61,54,39,0.5); color:#7a6f5d; font-size:9px; cursor:pointer; letter-spacing:.08em; font-family:inherit; transition:all 0.2s; }
        .irl-u-btn:hover { border-color:rgba(201,168,76,0.3); color:#c9a84c; }
        /* Toggle */
        .irl-toggle { display:flex; gap:2px; }
        .irl-toggle-btn { padding:3px 10px; background:transparent; border:1px solid rgba(61,54,39,0.4); color:#7a6f5d; font-size:8px; cursor:pointer; letter-spacing:.08em; font-family:inherit; transition:all 0.2s; }
        .irl-toggle-active { color:#c9a84c; border-bottom-color:#c9a84c; }
        /* Synthesis banner */
        .irl-synthesis-banner { display:flex; align-items:center; gap:8px; padding:5px 16px; background:rgba(10,25,41,0.4); border-bottom:1px solid rgba(201,168,76,0.05); flex-wrap:wrap; }
        .irl-synthesis-v1-label { font-size:8px; color:#2d8a87; letter-spacing:.08em; }
        .irl-synthesis-v2-label { font-size:8px; color:#5a5a8a; letter-spacing:.08em; }
        .irl-synthesis-bridge { font-size:10px; color:rgba(201,168,76,0.3); }
        .irl-synthesis-desc { font-size:8px; color:#7a6f5d; font-style:italic; }
        /* Tabs */
        .irl-tabs { display:flex; border-bottom:1px solid rgba(61,54,39,0.3); background:rgba(6,13,18,0.8); }
        .irl-tab { flex:1; padding:8px 4px; background:transparent; border:none; border-bottom:2px solid transparent; color:#7a6f5d; font-size:9px; cursor:pointer; letter-spacing:.1em; font-family:inherit; text-transform:uppercase; transition:all 0.2s; position:relative; }
        .irl-tab:hover { color:#d4c5a9; }
        .irl-tab-active { color:#c9a84c; border-bottom-color:#c9a84c; }
        .irl-tab-badge { margin-left:4px; background:rgba(45,138,135,0.2); color:#2d8a87; font-size:7px; padding:1px 4px; border-radius:2px; }
        .irl-tab-new { display:inline-block; width:5px; height:5px; background:#2d8a87; border-radius:50%; margin-left:3px; vertical-align:middle; }
        /* Lens body */
        .irl-u-body { display:flex; flex:1; overflow:hidden; position:relative; min-height:500px; }
        .irl-u-mosaic { flex:1; position:relative; overflow:hidden; }
        /* Tile grid */
        .irl-u-grid { display:flex; flex-direction:column; gap:1px; padding:8px; overflow-y:auto; max-height:480px; }
        .irl-u-biome-row { margin-bottom:8px; }
        .irl-u-biome-label { font-size:7px; color:#7a6f5d; letter-spacing:.1em; text-transform:uppercase; margin-bottom:4px; padding:0 2px; }
        .irl-u-tiles { display:flex; flex-wrap:wrap; gap:4px; }
        .irl-u-tile { padding:6px 8px; background:rgba(10,25,41,0.4); border:1px solid rgba(61,54,39,0.3); border-radius:3px; cursor:pointer; transition:all 0.15s; min-width:100px; max-width:160px; position:relative; }
        .irl-u-tile:hover { border-color:rgba(201,168,76,0.3); background:rgba(201,168,76,0.04); }
        .irl-u-tile-sel { border-color:rgba(201,168,76,0.6); background:rgba(201,168,76,0.08); }
        .irl-u-tile-bridge { border-color:rgba(45,138,135,0.4); }
        .irl-u-tl { font-size:10px; color:#d4c5a9; line-height:1.3; margin-bottom:2px; }
        .irl-u-tt { font-size:7px; color:#7a6f5d; letter-spacing:.06em; text-transform:uppercase; }
        .irl-u-tseq { font-size:7px; color:#5a5a8a; margin-bottom:2px; }
        /* Detail panel */
        .irl-u-det { width:280px; flex-shrink:0; border-left:1px solid rgba(61,54,39,0.3); overflow-y:auto; max-height:480px; background:rgba(6,13,18,0.6); }
        .irl-u-deth { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; border-bottom:1px solid rgba(61,54,39,0.2); position:sticky; top:0; background:rgba(6,13,18,0.95); z-index:2; }
        .irl-u-detb { padding:10px 12px; }
        .irl-u-cbtn { padding:2px 8px; background:transparent; border:1px solid rgba(61,54,39,0.4); color:#7a6f5d; font-size:8px; cursor:pointer; font-family:inherit; transition:all 0.2s; }
        .irl-u-cbtn:hover { border-color:rgba(201,168,76,0.25); color:#c9a84c; }
        .irl-u-lbl { font-size:7px; color:#7a6f5d; letter-spacing:.1em; text-transform:uppercase; margin:10px 0 4px; border-top:1px solid rgba(61,54,39,0.15); padding-top:8px; }
        .irl-u-bl { display:block; width:100%; text-align:left; padding:4px 6px; background:transparent; border:1px solid rgba(61,54,39,0.2); color:#d4c5a9; font-size:10px; cursor:pointer; font-family:inherit; margin-bottom:2px; border-radius:2px; transition:all 0.15s; }
        .irl-u-bl:hover { border-color:rgba(201,168,76,0.25); background:rgba(201,168,76,0.03); }
        .irl-u-bpill { font-size:7px; padding:1px 4px; border-radius:2px; margin-right:4px; }
        .irl-u-bpill-name { background:rgba(201,168,76,0.1); color:#c9a84c; border:1px solid rgba(201,168,76,0.2); }
        .irl-u-bpill-proc { background:rgba(45,138,135,0.1); color:#2d8a87; border:1px solid rgba(45,138,135,0.2); }
        .irl-u-bpill-res { background:rgba(45,122,71,0.1); color:#2d7a47; border:1px solid rgba(45,122,71,0.2); }
        /* Name panel */
        .irl-u-npan { width:0; overflow:hidden; transition:width 0.3s ease; border-left:1px solid transparent; background:rgba(6,13,18,0.6); }
        .irl-u-npo { width:240px; border-left-color:rgba(61,54,39,0.3); overflow-y:auto; max-height:480px; }
        .irl-u-nph { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; border-bottom:1px solid rgba(61,54,39,0.2); position:sticky; top:0; background:rgba(6,13,18,0.95); z-index:2; }
        .irl-u-npb { padding:10px 12px; }
        .irl-u-npa { font-size:28px; color:#c9a84c; font-family:'Amiri',Georgia,serif; direction:rtl; text-align:center; margin-bottom:4px; }
        .irl-u-npe { font-size:10px; color:#d4c5a9; text-align:center; margin-bottom:8px; }
        .irl-u-npl { font-size:7px; color:#7a6f5d; letter-spacing:.1em; text-transform:uppercase; margin:8px 0 3px; border-top:1px solid rgba(61,54,39,0.15); padding-top:6px; }
        .irl-u-npay { font-size:10px; color:rgba(212,197,169,0.7); line-height:1.5; padding:2px 0; font-style:italic; }
        .irl-u-npt { font-size:9px; color:#2d8a87; padding:1px 4px; background:rgba(45,138,135,0.08); border:1px solid rgba(45,138,135,0.15); border-radius:2px; margin-right:3px; display:inline-block; margin-bottom:2px; }
        .irl-u-nps { font-size:10px; color:rgba(212,197,169,0.6); line-height:1.5; }
        /* Observation panel */
        .irl-u-obs { position:absolute; right:0; top:0; bottom:0; width:0; overflow:hidden; transition:width 0.3s ease; background:rgba(6,13,18,0.97); border-left:1px solid transparent; z-index:10; }
        .irl-u-oo { width:320px; border-left-color:rgba(61,54,39,0.4); overflow-y:auto; }
        .irl-u-obh { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-bottom:1px solid rgba(61,54,39,0.3); position:sticky; top:0; background:rgba(6,13,18,0.98); z-index:2; }
        .irl-u-obb { padding:12px 14px; }
        .irl-u-obs-dots { display:flex; gap:4px; align-items:center; margin-bottom:10px; }
        .irl-u-obs-dot { height:4px; border-radius:2px; transition:all 0.2s; }
        .irl-u-inp { width:100%; padding:6px 8px; background:rgba(10,25,41,0.5); border:1px solid rgba(61,54,39,0.4); color:#d4c5a9; font-size:11px; font-family:inherit; outline:none; border-radius:2px; box-sizing:border-box; }
        .irl-u-inp:focus { border-color:rgba(201,168,76,0.3); }
        .irl-u-ta { width:100%; padding:6px 8px; background:rgba(10,25,41,0.5); border:1px solid rgba(61,54,39,0.4); color:#d4c5a9; font-size:11px; font-family:inherit; outline:none; border-radius:2px; resize:vertical; min-height:60px; box-sizing:border-box; }
        .irl-u-ta:focus { border-color:rgba(201,168,76,0.3); }
        .irl-u-name-grid { display:flex; flex-wrap:wrap; gap:4px; margin-top:4px; }
        .irl-u-name-pill { padding:3px 7px; background:rgba(10,25,41,0.4); border:1px solid rgba(61,54,39,0.3); color:#d4c5a9; font-size:10px; cursor:pointer; border-radius:2px; transition:all 0.15s; }
        .irl-u-name-pill:hover { border-color:rgba(201,168,76,0.3); }
        .irl-u-name-pill-sel { background:rgba(201,168,76,0.1); border-color:rgba(201,168,76,0.4); color:#c9a84c; }
        .irl-u-name-pill-name { background:rgba(201,122,71,0.1); color:#c9a84c; border-color:rgba(201,122,71,0.3); }
        .irl-u-name-pill-proc { background:rgba(45,138,135,0.08); color:#2d8a87; border-color:rgba(45,138,135,0.2); }
        .irl-u-name-pill-res { background:rgba(45,122,71,0.08); color:#2d7a47; border-color:rgba(45,122,71,0.2); }
        .irl-u-submit-btn { width:100%; padding:10px; background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.3); color:#c9a84c; font-size:10px; cursor:pointer; font-family:inherit; letter-spacing:.1em; text-transform:uppercase; transition:all 0.2s; border-radius:2px; }
        .irl-u-submit-btn:hover { background:rgba(201,168,76,0.25); }
        .irl-u-submit-btn:disabled { opacity:0.4; cursor:not-allowed; }
        .irl-u-nav-btn { padding:4px 10px; background:transparent; border:1px solid rgba(61,54,39,0.4); color:#7a6f5d; font-size:9px; cursor:pointer; font-family:inherit; transition:all 0.2s; }
        .irl-u-nav-btn:hover:not(:disabled) { border-color:rgba(201,168,76,0.3); color:#c9a84c; }
        .irl-u-nav-btn:disabled { opacity:0.3; cursor:not-allowed; }
        /* Layer nav */
        .irl-u-layer-nav { display:flex; align-items:center; gap:6px; padding:6px 12px; background:rgba(6,13,18,0.9); border-top:1px solid rgba(61,54,39,0.2); }
        .irl-u-layer-nav-btn { padding:3px 10px; background:transparent; border:1px solid rgba(61,54,39,0.4); color:#7a6f5d; font-size:10px; cursor:pointer; font-family:inherit; transition:all 0.2s; }
        .irl-u-layer-nav-btn:hover:not(:disabled) { border-color:rgba(201,168,76,0.3); color:#c9a84c; }
        .irl-u-layer-nav-btn:disabled { opacity:0.3; cursor:not-allowed; }
        .irl-u-layer-nav-divider { color:rgba(61,54,39,0.4); font-size:12px; }
        .irl-u-layer-nav-dot { width:8px; height:8px; border-radius:50%; cursor:pointer; transition:all 0.2s; }
        /* Ayah footer */
        .irl-u-ayf { padding:8px 16px; border-top:1px solid rgba(61,54,39,0.15); background:rgba(6,13,18,0.5); }
        .irl-u-ayf-t { font-size:9px; color:#7a6f5d; font-style:italic; text-align:center; }
        /* Golden ring / mosaic */
        .irl-u-gring { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); border-radius:50%; border:1px solid rgba(201,168,76,0.2); pointer-events:none; }
        .irl-u-ar { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); border-radius:50%; border:1px solid rgba(61,54,39,0.15); pointer-events:none; }
        .irl-u-nm { position:absolute; cursor:pointer; text-align:center; padding:3px 5px; background:rgba(6,13,18,0.7); border:1px solid rgba(61,54,39,0.3); border-radius:3px; transition:all 0.15s; min-width:40px; }
        .irl-u-nm:hover, .irl-u-nma { background:rgba(201,168,76,0.08); border-color:rgba(201,168,76,0.35); }
        .irl-u-nn { display:block; font-size:12px; color:#c9a84c; font-family:'Amiri',Georgia,serif; }
        .irl-u-ne { display:block; font-size:6px; color:#7a6f5d; letter-spacing:.04em; }
        /* Center env */
        .irl-u-env { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); border-radius:50%; background:rgba(6,13,18,0.85); border:2px solid rgba(45,122,71,0.3); cursor:pointer; overflow:hidden; transition:border-color 0.2s; }
        .irl-u-env:hover { border-color:rgba(45,122,71,0.6); }
        .irl-u-env-in { position:relative; width:100%; height:100%; display:flex; align-items:flex-end; justify-content:center; }
        .irl-u-crescent { position:absolute; top:12px; left:50%; transform:translateX(-50%); width:28px; height:28px; }
        .irl-u-cb { width:28px; height:28px; border-radius:50%; background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.3); }
        .irl-u-cs { position:absolute; top:2px; left:6px; width:24px; height:24px; border-radius:50%; background:#060d12; }
        .irl-u-wl { position:absolute; bottom:8px; left:0; right:0; text-align:center; }
        .irl-u-wl-n { font-size:9px; color:#c9a84c; letter-spacing:.08em; }
        .irl-u-wl-c { font-size:7px; color:#7a6f5d; }
        /* Physics overlay */
        .irl-u-phys-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.85); display:flex; align-items:center; justify-content:center; z-index:100; }
        .irl-u-phys-card { background:#0a0a1a; border:1px solid rgba(90,90,138,0.3); padding:24px; max-width:400px; width:90%; border-radius:6px; }
        .irl-u-phys-dismiss { width:100%; margin-top:16px; padding:10px; background:rgba(90,90,138,0.15); border:1px solid rgba(90,90,138,0.3); color:#8a8ab8; font-size:9px; cursor:pointer; font-family:inherit; letter-spacing:.1em; text-transform:uppercase; border-radius:3px; }
        .irl-u-phys-dismiss:hover { background:rgba(90,90,138,0.25); }
        /* Toast */
        .irl-toast { position:fixed; bottom:20px; right:20px; display:flex; flex-direction:column; gap:6px; z-index:200; }
        .irl-toast-item { padding:8px 12px; background:rgba(6,13,18,0.95); border:1px solid rgba(201,168,76,0.25); border-radius:4px; min-width:180px; }
        .irl-toast-title { font-size:10px; color:#c9a84c; margin-bottom:2px; }
        .irl-toast-sub { font-size:9px; color:#7a6f5d; }
        /* Catalog */
        .irl-catalog { padding:12px; overflow-y:auto; max-height:460px; }
        .irl-catalog-filters { display:flex; align-items:center; gap:6px; margin-bottom:10px; flex-wrap:wrap; }
        .irl-catalog-filter-input { padding:4px 8px; background:rgba(10,25,41,0.5); border:1px solid rgba(61,54,39,0.4); color:#d4c5a9; font-size:9px; font-family:inherit; outline:none; border-radius:2px; width:140px; }
        .irl-catalog-filter-btn { padding:3px 8px; background:transparent; border:1px solid rgba(61,54,39,0.3); color:#7a6f5d; font-size:8px; cursor:pointer; font-family:inherit; border-radius:2px; transition:all 0.15s; }
        .irl-catalog-filter-btn.active { border-color:rgba(201,168,76,0.4); color:#c9a84c; background:rgba(201,168,76,0.06); }
        .irl-obs-empty { text-align:center; padding:32px 16px; color:#7a6f5d; font-size:11px; }
        .irl-obs-card { border:1px solid rgba(61,54,39,0.25); border-radius:3px; margin-bottom:6px; overflow:hidden; transition:border-color 0.15s; }
        .irl-obs-card:hover { border-color:rgba(201,168,76,0.2); }
        .irl-obs-card.new-entry { border-color:rgba(45,138,135,0.4); animation:newEntry 1s ease; }
        @keyframes newEntry { from { background:rgba(45,138,135,0.08); } to { background:transparent; } }
        .irl-obs-card-header { display:flex; align-items:center; padding:8px 10px; cursor:pointer; gap:8px; }
        .irl-obs-card-title { font-size:11px; color:#d4c5a9; margin-bottom:2px; }
        .irl-obs-card-meta { font-size:8px; color:#7a6f5d; }
        .irl-obs-card-names { display:flex; flex-wrap:wrap; gap:3px; margin-top:4px; }
        .irl-obs-card-name-pill { font-size:10px; color:#c9a84c; padding:1px 4px; background:rgba(201,168,76,0.06); border:1px solid rgba(201,168,76,0.15); border-radius:2px; cursor:pointer; }
        .irl-obs-card-expand-btn { font-size:12px; color:#7a6f5d; transition:transform 0.2s; flex-shrink:0; }
        .irl-obs-card-expand-btn.open { transform:rotate(180deg); }
        .irl-obs-card-layers { padding:8px 10px; border-top:1px solid rgba(61,54,39,0.15); display:flex; flex-direction:column; gap:6px; }
        .irl-obs-layer-row { display:flex; flex-direction:column; gap:2px; }
        .irl-obs-layer-label { display:flex; align-items:center; gap:5px; font-size:7px; color:#7a6f5d; letter-spacing:.08em; text-transform:uppercase; margin-bottom:2px; }
        .irl-obs-layer-num { width:14px; height:14px; border-radius:50%; background:rgba(61,54,39,0.4); display:flex; align-items:center; justify-content:center; font-size:7px; color:#c9a84c; flex-shrink:0; }
        .irl-obs-layer-text { font-size:10px; color:rgba(212,197,169,0.7); line-height:1.5; padding:2px 0; }
        /* Streams */
        .irl-streams { padding:12px; overflow-y:auto; max-height:460px; }
        .irl-streams-section { margin-bottom:16px; }
        .irl-streams-section-title { font-size:8px; color:#7a6f5d; letter-spacing:.1em; text-transform:uppercase; margin-bottom:8px; border-bottom:1px solid rgba(61,54,39,0.2); padding-bottom:4px; }
        .irl-stream-filters { display:flex; gap:4px; flex-wrap:wrap; margin-bottom:8px; }
        .irl-stream-filter { padding:2px 8px; background:transparent; border:1px solid rgba(61,54,39,0.3); color:#7a6f5d; font-size:7px; cursor:pointer; font-family:inherit; border-radius:2px; transition:all 0.15s; }
        .irl-stream-filter.active { border-color:rgba(201,168,76,0.4); color:#c9a84c; background:rgba(201,168,76,0.06); }
        /* Heatmap */
        .irl-heatmap { display:flex; flex-wrap:wrap; gap:4px; }
        .irl-heatmap-cell { padding:4px 6px; border:1px solid; border-radius:3px; cursor:pointer; text-align:center; transition:all 0.15s; }
        .irl-heatmap-cell:hover { opacity:0.8; }
        .irl-heatmap-ar { display:block; font-size:14px; color:#c9a84c; font-family:'Amiri',Georgia,serif; }
        .irl-heatmap-en { display:block; font-size:7px; color:#7a6f5d; }
        .irl-heatmap-count { display:block; font-size:7px; color:rgba(201,168,76,0.5); }
        /* Bridge suggestion */
        .irl-bridge-suggestion { padding:8px 10px; background:rgba(10,25,41,0.3); border:1px solid rgba(61,54,39,0.2); border-radius:3px; margin-bottom:6px; }
        .irl-bridge-suggestion-title { font-size:10px; color:#d4c5a9; margin-bottom:3px; }
        .irl-bridge-suggestion-desc { font-size:9px; color:#7a6f5d; }
        /* Live dot */
        .irl-live-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:#2d8a87; animation:livePulse 2s ease-in-out infinite; flex-shrink:0; }
        @keyframes livePulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        /* Walkthrough skeleton pulse */
        @keyframes irl-skeleton-pulse { 0%,100% { opacity:0.4; } 50% { opacity:0.8; } }
        /* Walkthrough dot pulse */
        @keyframes irl-dot-pulse { 0%,100% { box-shadow:0 0 6px rgba(201,168,76,0.4),0 0 12px rgba(201,168,76,0.15); transform:scale(1); } 50% { box-shadow:0 0 10px rgba(201,168,76,0.7),0 0 20px rgba(201,168,76,0.3); transform:scale(1.15); } }
        /* Scrollbar */
        .irl-u *::-webkit-scrollbar { width:4px; height:4px; }
        .irl-u *::-webkit-scrollbar-track { background:rgba(6,13,18,0.5); }
        .irl-u *::-webkit-scrollbar-thumb { background:rgba(61,54,39,0.5); border-radius:2px; }
      `}</style>
      {showPhysGuide && (
        <PhysicsGuideOverlay onDismiss={() => {
          if (typeof window !== 'undefined') localStorage.setItem('haal_physics_guide_seen', '1');
          setShowPhysGuide(false);
        }} />
      )}
      {showWalkthrough && (
        <WalkthroughPanel onClose={() => { setShowWalkthrough(false); setWalkthroughDismissed(true); }} />
      )}
      <ToastContainer toasts={toasts} />
      <div className="irl-u">
        {/* HEADER */}
        <div className="irl-u-hd">
          <div>
            <div className="irl-u-hd-t">INNER REALITY LENS</div>
            <div className="irl-u-hd-s">{variant === 'v1' ? '7-Layer Scroll · Ayat in Creation' : '8-Layer Recognition Pathway · Fission Sequence'}</div>
          </div>
          <div className="irl-u-hd-ar">عدسة الحقيقة الداخلية</div>
          {/* Walkthrough button — always accessible */}
          <button
            onClick={() => setShowWalkthrough(true)}
            style={{
              padding: '4px 10px',
              background: walkthroughDismissed ? 'transparent' : 'rgba(201,168,76,0.1)',
              border: `1px solid ${walkthroughDismissed ? 'rgba(61,54,39,0.5)' : 'rgba(201,168,76,0.25)'}`,
              color: walkthroughDismissed ? '#7a6f5d' : '#c9a84c',
              fontSize: 9,
              cursor: 'pointer',
              borderRadius: 3,
              letterSpacing: '.08em',
              fontFamily: 'inherit',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
            title="Open IRL Lens Walkthrough"
          >
            {walkthroughDismissed ? '? Walkthrough' : '✦ Walkthrough'}
          </button>
          <div className="irl-toggle">
            <button
              className={`irl-toggle-btn irl-toggle-v1${variant === 'v1' ? ' irl-toggle-active' : ''}`}
              onClick={() => { setVariant('v1'); setSelTile(null); setActiveLayer(-1); setActiveBridge(null); setFilter('all'); }}
            >
              V1 · 7-LAYER
            </button>
            <button
              className={`irl-toggle-btn irl-toggle-v2${variant === 'v2' ? ' irl-toggle-active' : ''}`}
              onClick={() => { setVariant('v2'); setSelTile(null); setActiveLayer(-1); setActiveBridge(null); setFilter('all'); }}
            >
              V2 · 8-LAYER
            </button>
          </div>
          <Link href="/muslim-resource-guide">
            <button className="irl-u-btn">← MRG</button>
          </Link>
        </div>

        {/* SYNTHESIS BANNER */}
        <div className="irl-synthesis-banner">
          <span className="irl-synthesis-v1-label">V1: 7 LAYERS · COMMUNITY LENS</span>
          <span className="irl-synthesis-bridge">⟷</span>
          <span className="irl-synthesis-v2-label">V2: 8 LAYERS · FISSION PATHWAY</span>
          <span className="irl-synthesis-bridge">·</span>
          <span className="irl-synthesis-desc">Both variations share the same Names — the same divine attributes compose the ward and the forest, the community and the cosmos.</span>
        </div>

        {/* ONBOARDING: MATA + LEAF SEQUENCE + RECOGNITION ARC */}
        {!selTile && activeTab === 'lens' && (
          <div style={{ padding: '8px 16px', background: 'rgba(6,13,18,0.5)', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
            {/* MATA concept + webpage metaphor */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <div style={{ padding: '2px 8px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 3, fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', flexShrink: 0, marginTop: 1 }}>
                مَاطَا · MATA
              </div>
              <div style={{ fontSize: 9, color: '#7a6f5d', lineHeight: 1.5 }}>
                The Lens as Tool — how you look determines what you see.{' '}
                <span style={{ color: 'rgba(201,168,76,0.5)', fontStyle: 'italic' }}>A webpage presupposes an author. Creation presupposes a Designer. The Lens reads the design.</span>
                {' '}Select any encounter tile to begin.
              </div>
              <button
                onClick={() => setShowWalkthrough(true)}
                style={{
                  padding: '2px 8px',
                  background: 'rgba(45,122,71,0.08)',
                  border: '1px solid rgba(45,122,71,0.25)',
                  color: '#2d7a47',
                  fontSize: 8,
                  cursor: 'pointer',
                  borderRadius: 3,
                  fontFamily: 'inherit',
                  flexShrink: 0,
                  letterSpacing: '.06em',
                }}
              >
                ✦ Demo
              </button>
            </div>
            {/* Recognition Arc stages — now includes Re-Assembly */}
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {RECOGNITION_ARC_STAGES.map((stage, i) => (
                  <React.Fragment key={stage.id}>
                    <div style={{
                      flex: 1, padding: '3px 4px',
                      background: i === 0 ? 'rgba(201,168,76,0.08)' : stage.id === 'reassembly' ? 'rgba(90,90,138,0.06)' : stage.id === 'uboor' ? 'rgba(45,122,71,0.06)' : 'rgba(6,13,18,0.3)',
                      border: `1px solid ${i === 0 ? 'rgba(201,168,76,0.25)' : stage.id === 'reassembly' ? 'rgba(90,90,138,0.2)' : stage.id === 'uboor' ? 'rgba(45,122,71,0.2)' : 'rgba(61,54,39,0.2)'}`,
                      borderRadius: 3, textAlign: 'center', position: 'relative',
                    }}>
                      <div style={{
                        fontSize: 6, color: i === 0 ? '#c9a84c' : stage.id === 'reassembly' ? '#8a8ab8' : stage.id === 'uboor' ? '#2d7a47' : '#7a6f5d', letterSpacing: '.06em', textTransform: 'uppercase', lineHeight: 1.2,
                      }}>
                        {stage.label}
                      </div>
                      <div style={{
                        fontSize: 7, color: i === 0 ? 'rgba(201,168,76,0.6)' : stage.id === 'reassembly' ? 'rgba(138,138,184,0.5)' : stage.id === 'uboor' ? 'rgba(45,122,71,0.6)' : 'rgba(122,111,93,0.4)', direction: 'rtl',
                      }}>
                        {stage.ar}
                      </div>
                    </div>
                    {/* Progression line between stages */}
                    {i < RECOGNITION_ARC_STAGES.length - 1 && (
                      <div style={{
                        width: 6, height: 1, flexShrink: 0,
                        background: i === 0 ? 'rgba(201,168,76,0.4)' : stage.id === 'reassembly' ? 'rgba(90,90,138,0.3)' : stage.id === 'uboor' ? 'rgba(45,122,71,0.3)' : 'rgba(61,54,39,0.3)',
                        transition: 'background 0.4s ease',
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            {/* Leaf Sequence tutorial */}
            <div style={{ padding: '6px 8px', background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 7, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase' }}>✦ LEAF SEQUENCE — Sunnat Tutorial</div>
                <button
                  onClick={() => setShowWalkthrough(true)}
                  style={{
                    padding: '1px 6px',
                    background: 'transparent',
                    border: '1px solid rgba(45,122,71,0.25)',
                    color: '#2d7a47',
                    fontSize: 7,
                    cursor: 'pointer',
                    borderRadius: 2,
                    fontFamily: 'inherit',
                  }}
                >
                  Full Walkthrough →
                </button>
              </div>
              <div style={{ display: 'flex', gap: 3, overflow: 'hidden', flexWrap: 'wrap' }}>
                {LEAF_SEQUENCE.map((item, i) => (
                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <div style={{
                      padding: '2px 5px',
                      background: 'rgba(6,13,18,0.4)',
                      border: `1px solid ${item.color}30`,
                      borderRadius: 3,
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 10, color: item.color }}>{item.ar}</div>
                      <div style={{ fontSize: 6, color: 'rgba(212,197,169,0.4)', letterSpacing: '.04em' }}>
                        {item.name.replace('Al-', '').replace('Ar-', '').replace('An-', '')}
                      </div>
                    </div>
                    {i < LEAF_SEQUENCE.length - 1 && <span style={{ fontSize: 8, color: 'rgba(201,168,76,0.2)' }}>→</span>}
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 4 }}>
                  <span style={{ fontSize: 8, color: 'rgba(201,168,76,0.3)' }}>→</span>
                  <div style={{
                    padding: '2px 5px',
                    background: 'rgba(201,168,76,0.06)',
                    border: '1px solid rgba(201,168,76,0.2)',
                    borderRadius: 3,
                  }}>
                    <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.06em' }}>الحمدلله</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MAIN TABS */}
        <div className="irl-tabs">
          <button className={`irl-tab${activeTab === 'lens' ? ' irl-tab-active' : ''}`} onClick={() => setActiveTab('lens')}>
            LENS
          </button>
          <button className={`irl-tab${activeTab === 'catalog' ? ' irl-tab-active' : ''}`} onClick={() => { setActiveTab('catalog'); setNewObsCount(0); }}>
            CATALOG
            {observations.length > 0 && <span className="irl-tab-badge">{observations.length}</span>}
            {newObsCount > 0 && <span className="irl-tab-new" />}
          </button>
          <button className={`irl-tab${activeTab === 'streams' ? ' irl-tab-active' : ''}`} onClick={() => setActiveTab('streams')}>
            STREAMS
          </button>
        </div>

        {/* CATALOG TAB */}
        {activeTab === 'catalog' && (
          <CatalogTab
            observations={observations}
            latestId={latestObsId}
            onNameClick={handleNameById}
          />
        )}

        {/* STREAMS TAB */}
        {activeTab === 'streams' && (
          <StreamsTab
            observations={observations}
            onNameClick={handleNameById}
            onTileClick={handleFissionTileClick}
          />
        )}

        {/* LENS TAB */}
        {activeTab === 'lens' && (
          <>
            {/* SEARCH BAR */}
            <div className="irl-search-bar" ref={searchRef} style={{ position: 'relative' }}>
              <input
                className="irl-search-input"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowSearch(true); }}
                onFocus={() => setShowSearch(true)}
                placeholder="Search by title, city, biome, process name, Names..."
              />
              {showSearch && searchQuery && (
                <SearchIndex query={searchQuery} onSelect={handleSearchSelect} />
              )}
            </div>

            {/* CITY SELECTOR */}
            <div className="irl-u-sel">
              {CITIES.map(c => (
                <button key={c} className={`irl-u-st${c === cityId ? ' irl-u-sa' : ''}`}
                  onClick={() => { setCityId(c); setSelTile(null); setSelName(null); setNameOpen(false); setActiveLayer(-1); setActiveBridge(null); setFilter('all'); }}>
                  {c}
                </button>
              ))}
            </div>

            {/* BIOME / FILTER BAR */}
            <div className="irl-u-bbar">
              <div className="irl-u-bdot" style={{ background: variant === 'v2' && filter === 'phys' ? '#5a5a8a' : '#2d7a47' }} />
              <div className="irl-u-bname" style={{ color: variant === 'v2' && filter === 'phys' ? '#5a5a8a' : '#2d7a47' }}>
                {variant === 'v2' && filter === 'phys' ? 'Physics / Quantum Layer' : (biome.name || cityId)}
              </div>
              <div className="irl-u-bar2">{variant !== 'v2' || filter !== 'phys' ? biome.ar : ''}</div>
              <div className="irl-u-bcount">
                <span className={`irl-u-variant-badge ${variant === 'v1' ? 'irl-u-v1-badge' : 'irl-u-v2-badge'}`}>
                  {variant === 'v1' ? 'V1 · 7-LAYER SCROLL' : 'V2 · 8-LAYER FISSION'}
                </span>
              </div>
            </div>

            {/* SCENE */}
            <div className="irl-u-scene" ref={mRef}>
              <div className="irl-u-txtl" ref={scrollRef}>
                <div className="irl-u-txti">
                  {ct ? (
                    isFissionTile && variant === 'v2'
                      ? renderV2Detail(ct as typeof FISSION_SEQ[0])
                      : renderV1Detail(ct as AnyTile)
                  ) : (
                    <div style={{ textAlign: 'center', paddingTop: 20 }}>
                      <div style={{ fontSize: 14, color: 'rgba(201,168,76,0.3)', fontStyle: 'italic', lineHeight: 1.6 }}>
                        Soon We will show them Our signs in the horizons and in themselves until it becomes clear that it is the Truth. (41:53)
                        <div style={{ fontSize: 11, color: '#7a6f5d', marginTop: 8 }}>
                          {variant === 'v2' ? 'Select an encounter above to begin the 8-layer recognition pathway.' : 'Select a tile above to explore its Haal domain.'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* MOSAIC */}
              {sz > 0 && (
                <div ref={mosaicRef} className="irl-u-mosaic" style={{ width: mosaicW, height: mosaicH, position: 'absolute' }}>
                  {makeRows().map((row, ri) => (
                    <div key={ri} style={{ position: 'relative' }}>
                      {row.label && <div className="irl-u-trow-lbl">{row.label}</div>}
                      <div className="irl-u-trow" style={{ position: 'absolute', top: row.y, height: row.h }}>
                        {row.tiles.map(t => {
                          const isSel = selTile?.id === t.id;
                          const isBridgeTarget = activeBridge === t.id;
                          const isPhysTile = 'biome' in t && ((t as typeof FISSION_SEQ[0]).biome === 'physics' || (t as typeof FISSION_SEQ[0]).biome === 'quantum');
                          const tileClass = isPhysTile ? 'irl-u-phys-t' : t.type === 'eco' ? 'irl-u-eco-t' : 'irl-u-comm-t';
                          if (row.speck) return (
                            <div key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
                              <div
                                ref={el => { tileRefs.current[t.id] = el; }}
                                style={{ width: 10, height: 10, borderRadius: '50%', background: t.bg, cursor: 'pointer', outline: isSel ? '1px solid #c9a84c' : 'none' }}
                                onClick={() => handleTile(t)} title={t.title}
                              />
                            </div>
                          );
                          return (
                            <div
                              key={t.id}
                              ref={el => { tileRefs.current[t.id] = el; }}
                              className={`irl-u-tc ${tileClass}${isSel ? ' irl-u-tcs' : ''}${isBridgeTarget ? ' irl-u-bridge-target' : ''}`}
                              style={{ background: t.bg, height: '100%' }}
                              onClick={() => handleTile(t)}
                            >
                              {isBridgeTarget && <div style={{ position: 'absolute', top: -14, left: 0, right: 0, textAlign: 'center', fontSize: 7, color: '#c9a84c', zIndex: 10 }}>← connected</div>}
                              {'seq' in t && <div className="irl-u-tseq">{(t as typeof FISSION_SEQ[0]).seq}</div>}
                              <div className="irl-u-tl">{t.title}</div>
                              <div className="irl-u-tt">{t.cat}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* SVG Bridge Connector */}
                  {activeBridge && srcPos && tgtPos && activeBridgeObj && (
                    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 20, overflow: 'visible' }}>
                      <path
                        d={`M ${srcPos.x} ${srcPos.y} Q ${(srcPos.x + tgtPos.x) / 2} ${(srcPos.y + tgtPos.y) / 2 - 40} ${tgtPos.x} ${tgtPos.y}`}
                        stroke={activeBridgeObj.type === 'name' ? 'rgba(201,168,76,0.6)' : activeBridgeObj.type === 'process' ? 'rgba(45,138,135,0.6)' : 'rgba(45,122,71,0.6)'}
                        strokeWidth={1.5} fill="none"
                      />
                    </svg>
                  )}

                  {/* GOLDEN RING */}
                  <div className="irl-u-gring" style={{ width: ringW, height: ringW }} />
                  <div className="irl-u-ar" style={{ width: ringW * 1.08, height: ringW * 1.08 }} />
                  <div className="irl-u-ar" style={{ width: ringW * 0.92, height: ringW * 0.92 }} />

                  {/* NAME NODES */}
                  {ringNames.map((n, i) => {
                    const angle = (i / ringNames.length) * Math.PI * 2 - Math.PI / 2;
                    const r = ringW * 0.53;
                    const cx = mosaicW / 2;
                    const cy = mosaicH / 2;
                    const x = cx + r * Math.cos(angle);
                    const y = cy + r * Math.sin(angle);
                    const isA = selName?.id === n.id;
                    return (
                      <div key={n.id} className={`irl-u-nm${isA ? ' irl-u-nma' : ''}`}
                        style={{ left: x, top: y, transform: 'translate(-50%,-50%)' }}
                        onClick={() => handleName(n)}>
                        <span className="irl-u-nn">{n.ar}</span>
                        <span className="irl-u-ne">{n.m.split(' ').pop()}</span>
                      </div>
                    );
                  })}

                  {/* CENTER ENV */}
                  <div className="irl-u-env" style={{ width: envW, height: envW }} onClick={() => setObsOpen(true)}>
                    <div className="irl-u-env-in">
                      <div className="irl-u-crescent"><div className="irl-u-cb" /><div className="irl-u-cs" /></div>
                      <svg viewBox="0 0 200 70" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%' }}>
                        <path d="M0,40 Q30,15 60,32 Q80,22 100,28 Q130,10 160,32 Q180,22 200,38 L200,70 L0,70Z" fill="rgba(26,92,90,0.3)" />
                        <path d="M0,50 Q40,35 80,45 Q120,38 160,48 Q180,42 200,52 L200,70 L0,70Z" fill="rgba(26,92,90,0.5)" />
                      </svg>
                      <div className="irl-u-wl">
                        <div className="irl-u-wl-n">{cityId}</div>
                        <div className="irl-u-wl-c">{variant === 'v2' && filter === 'phys' ? 'Physics Layer' : (biome.name || 'Tap to observe')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* NAME PANEL */}
              <div className={`irl-u-npan${nameOpen && selName ? ' irl-u-npo' : ''}`}>
                {selName && (
                  <div>
                    <div className="irl-u-nph">
                      <span style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em' }}>NAME / ATTRIBUTE</span>
                      <button className="irl-u-cbtn" onClick={() => { setNameOpen(false); setSelName(null); }}>Close</button>
                    </div>
                    <div className="irl-u-npb">
                      <div className="irl-u-npa">{selName.ar}</div>
                      <div className="irl-u-npe">{selName.en} — {selName.m}</div>
                      <div style={{ textAlign: 'center', marginTop: 4 }}>
                        <span style={{ fontSize: 9, color: CATCOL[selName.cat] || '#d4c5a9', border: `1px solid ${CATCOL[selName.cat] || '#d4c5a9'}`, padding: '1px 6px', borderRadius: 2 }}>→ {selName.cat}</span>
                      </div>
                      <div className="irl-u-npl">QURANIC AYAT</div>
                      {selName.ayat.map((a, i) => <div key={i} className="irl-u-npay">{a}</div>)}
                      <div className="irl-u-npl">SUNNAT ALLAH PATTERNS</div>
                      <div>→ {selName.procs.map((p, i) => <span key={i} className="irl-u-npt">{p}</span>)}</div>
                      <div className="irl-u-npl">SIMILARITIES ACROSS CREATION</div>
                      <div className="irl-u-nps">{selName.sim}</div>
                      <div className="irl-u-npl">FIELD PROMPT</div>
                      <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.5, fontStyle: 'italic', padding: '4px 0' }}>{selName.field}</div>
                      {/* V1/V2 SYNTHESIS: show both */}
                      <div className="irl-u-npl" style={{ marginTop: 12, color: '#5a5a8a' }}>V2 FISSION SEQUENCES WITH THIS NAME</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {FISSION_SEQ.filter(t => t.names.includes(selName.id)).slice(0, 8).map(t => (
                          <div key={t.id}
                            style={{ padding: '4px 6px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(90,90,138,0.1)', borderRadius: 3, cursor: 'pointer', fontSize: 11, color: '#d4c5a9' }}
                            onClick={() => { setVariant('v2'); setSelTile(t); setActiveLayer(0); setVisitedLayers(new Set([0])); }}>
                            <span style={{ color: '#5a5a8a', fontSize: 9 }}>s{t.seq} </span>{t.title} <span style={{ color: '#7a6f5d' }}>({t.loc})</span>
                          </div>
                        ))}
                      </div>
                      <div className="irl-u-npl" style={{ marginTop: 8, color: '#2d8a87' }}>V1 COMMUNITY TILES WITH THIS NAME</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {ALL_TILES.filter(t => t.names.includes(selName.id)).slice(0, 6).map(t => (
                          <div key={t.id}
                            style={{ padding: '4px 6px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(45,138,135,0.08)', borderRadius: 3, cursor: 'pointer', fontSize: 11, color: '#d4c5a9' }}
                            onClick={() => { setVariant('v1'); if ('city' in t) setCityId(t.city); setSelTile(t); setNameOpen(false); setSelName(null); }}>
                            {t.title} <span style={{ color: '#7a6f5d' }}>({'city' in t ? t.city : cityId})</span>
                          </div>
                        ))}
                      </div>
                      {/* Community observations with this Name */}
                      {observations.filter(o => (Array.isArray(o.selected_names) ? o.selected_names : []).includes(selName.id)).length > 0 && (
                        <>
                          <div className="irl-u-npl" style={{ marginTop: 8, color: '#c9a84c' }}>COMMUNITY OBSERVATIONS</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {observations.filter(o => (Array.isArray(o.selected_names) ? o.selected_names : []).includes(selName.id)).slice(0, 4).map(o => (
                              <div key={o.id} style={{ fontSize: 10, color: '#7a6f5d', padding: '2px 0', borderTop: '1px solid rgba(61,54,39,0.15)' }}>
                                <span style={{ color: '#d4c5a9' }}>{o.title}</span> · {o.city}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* OBSERVATION PANEL */}
              <ObsPanel open={obsOpen} onClose={() => setObsOpen(false)} cityId={cityId} variant={variant} />

              {/* LAYER NAV (V2 only) */}
              {ct && variant === 'v2' && !obsOpen && (
                <div className="irl-u-layer-nav">
                  <button className="irl-u-layer-nav-btn" disabled={activeLayer <= 0} onClick={() => goToLayer(Math.max(0, activeLayer - 1))}>←</button>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                    <div style={{ fontSize: 12, color: '#d4c5a9' }}>{activeLayer >= 0 ? activeLayer + 1 : '—'} / 8</div>
                    <div style={{ fontSize: 10, color: '#c9a84c', letterSpacing: '.06em' }}>{activeLayer >= 0 ? LAYER_LABELS[activeLayer] : 'SELECT LAYER'}</div>
                  </div>
                  <button className="irl-u-layer-nav-btn" disabled={activeLayer >= 7} onClick={() => goToLayer(Math.min(7, activeLayer + 1))}>→</button>
                  <div className="irl-u-layer-nav-divider">|</div>
                  {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="irl-u-layer-nav-dot" title={LAYER_LABELS[i]}
                      style={{ background: i === activeLayer ? '#c9a84c' : visitedLayers.has(i) ? 'transparent' : 'rgba(61,54,39,0.6)', border: i === activeLayer ? 'none' : visitedLayers.has(i) ? '1px solid rgba(201,168,76,0.5)' : '1px solid transparent' }}
                      onClick={() => goToLayer(i)}
                    />
                  ))}
                </div>
              )}

              {/* AYAH FOOTER */}
              {!obsOpen && !selTile && !nameOpen && (
                <div className="irl-u-ayf">
                  <div className="irl-u-ayf-t">If you subdivide and open up any part of reality, there are the Names and Attributes of Allah that compose it.</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

const IRLUnifiedScreen: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: IRLUnifiedScreen is not implemented yet.');
  }, []);
  return (
    <div>
      {/* IRLUnifiedScreen placeholder */}
    </div>
  );
};

export default IRLUnifiedScreen;